import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setCredentials, logout } from "../slices/authSlice";
import { encode } from "base-64";
import i18n from "i18next";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState, endpoint, extra }) => {
    headers.set("Accept-Language", i18n.language);
    const token = getState().auth.token;
    if (endpoint !== "login") {
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
    } else {
      headers.set(
        "authorization",
        `Basic ${encode(process.env.REACT_APP_BASIC_AUTH)}`
      );
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  // args = request url, method, body
  // api = signal, dispatch, getState()
  // extraOptions = custom like {shout: true }
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // send refresh token to get new access token
    let refreshToken = api.getState().auth.refreshToken;

    // No refresh token -> nothing to refresh with. Log out and return the
    // original 401 instead of firing a guaranteed-to-fail /login call.
    if (!refreshToken) {
      api.dispatch(logout());
      return result;
    }

    let formData = new FormData();
    formData.append("refresh_token", refreshToken);
    formData.append("grant_type", "refresh_token");

    const refreshResult = await baseQuery(
      { url: "/login", method: "POST", body: formData },
      { ...api, endpoint: "login" },
      extraOptions
    );

    if (refreshResult?.data) {
      // store the new token, then retry the original query with it
      api.dispatch(setCredentials({ ...refreshResult.data }));
      result = await baseQuery(args, api, extraOptions);
    } else {
      // refresh failed (e.g. expired refresh token) -> log out and return the
      // original 401; do NOT retry anonymously (that hits unguarded endpoints
      // like /getNextVip and logs spurious record.notFound errors).
      api.dispatch(logout());
    }
  }
  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({}),
});
